import React, { useEffect, useState } from 'react'
import { client } from '../../client/client'
import { Psychologist } from '../../types'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { DataGrid, GridColDef } from '@material-ui/data-grid'

interface AggregatedActivity {
  id: string
  date: string
  psychologistId: number
  psychologistName: string
  totalDuration: string
  activityCount: string
}

const AggregatedActivitiesPage: React.FC = () => {
  const [activities, setActivities] = useState<AggregatedActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [psychologists, setPsychologists] = useState<Psychologist[]>([])
  const [selectedPsychologist, setSelectedPsychologist] = useState<number | ''>(
    ''
  )
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const psychologistsResponse = await client.getPsychologistOptions()
        setPsychologists(psychologistsResponse)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true)
        const response = await client.getDailyRegisteredActivities(
          selectedPsychologist || undefined,
          startDate || undefined,
          endDate || undefined,
          pagination.page,
          pagination.pageSize
        )

        // Group by date and psychologist
        const grouped = response.data.reduce((acc, activity) => {
          const key = `${activity.date}-${activity.psychologistId}`
          if (!acc[key]) {
            acc[key] = {
              id: key,
              date: activity.date,
              psychologistId: activity.psychologistId,
              psychologistName: activity.psychologistName,
              totalDuration: '0',
              activityCount: '0'
            }
          }
          // Convert strings to numbers, add, then convert back to string
          acc[key].totalDuration = (
            parseInt(acc[key].totalDuration) + parseInt(activity.totalDuration)
          ).toString()
          acc[key].activityCount = (
            parseInt(acc[key].activityCount) + parseInt(activity.activityCount)
          ).toString()
          return acc
        }, {} as Record<string, AggregatedActivity>)

        const activitiesWithId = Object.values(grouped)

        setActivities(activitiesWithId)
        setPagination((prev) => ({
          ...prev,
          total: response.total
        }))
      } catch (error) {
        console.error('Error fetching registered activities:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchActivities()
  }, [
    selectedPsychologist,
    startDate,
    endDate,
    pagination.page,
    pagination.pageSize
  ])

  const getFullName = (psychologist: Psychologist) => {
    if (!psychologist.user) {
      return ''
    }
    const { firstName, infix, lastName } = psychologist.user
    return [firstName, infix, lastName].filter(Boolean).join(' ')
  }

  const columns: GridColDef[] = [
    {
      field: 'psychologistName',
      headerName: 'Psychologist',
      flex: 1,
      sortable: false
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      sortable: false,
      valueFormatter: (params) => {
        return new Date(params.value as string).toLocaleDateString()
      }
    },
    {
      field: 'totalDuration',
      headerName: 'Total Duration (minutes)',
      flex: 1,
      sortable: false
    },
    {
      field: 'activityCount',
      headerName: 'Number of Activities',
      flex: 1,
      sortable: false
    }
  ]

  const handleClearFilters = () => {
    setSelectedPsychologist('')
    setStartDate('')
    setEndDate('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  if (loading && !activities.length) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Box p={3} style={{ width: '100%', overflowX: 'auto', paddingTop: '5rem' }}>
      <Typography variant="h4" gutterBottom>
        Activities by Date
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Psychologist</InputLabel>
            <Select
              value={selectedPsychologist}
              onChange={(e) =>
                setSelectedPsychologist(e.target.value as number)
              }
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="">
                <em>All Psychologists</em>
              </MenuItem>
              {psychologists.map((psychologist) => (
                <MenuItem key={psychologist.id} value={psychologist.id}>
                  {getFullName(psychologist)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClearFilters}
            style={{ height: '100%', width: '100%' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
      <div style={{ width: '100%' }}>
        <DataGrid
          rows={activities}
          columns={columns}
          pageSize={pagination.pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          paginationMode="server"
          rowCount={pagination.total}
          onPageChange={(newPage) =>
            setPagination((prev) => ({ ...prev, page: newPage + 1 }))
          }
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({ ...prev, pageSize: newPageSize }))
          }
          loading={loading}
          disableSelectionOnClick
          disableColumnMenu
          autoHeight
        />
      </div>
    </Box>
  )
}

export default AggregatedActivitiesPage
